import { useMemo } from 'react';
import { Box, Card, CardActionArea, Chip, CircularProgress, Typography } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { NO05StatusStat } from '../../../models/NO05';
import { STATUS_GROUPS, statusInGroup } from '../no05StatusGroups';
import { DateBucket } from '../no05Stats';
function aggregateStats(list: NO05StatusStat[]) {
  const count      = list.reduce((s, x) => s + x.count, 0);
  const totalAmount = list.reduce((s, x) => s + x.totalAmount, 0);
  const antBestilt  = list.reduce((s, x) => s + x.antBestilt, 0);
  const antLevert   = list.reduce((s, x) => s + x.antLevert, 0);
  // Combine in kroner, the way ORVI does (dekningsbidrag / dekningsgrunnlag) — not by averaging
  // the per-group percentages. x.totalAmount is the wrong weight for x.brfProsent: it includes
  // surcharge and Frakt, which brfOmsetning excludes (decided 2026-09-21), and is rounded per
  // line total rather than per unit. See NO05.ts's brfOmsetning doc comment for what's excluded.
  const brfOmsetning = list.reduce((s, x) => s + x.brfOmsetning, 0);
  const brfKost      = list.reduce((s, x) => s + x.brfKost, 0);
  const brfProsent   = brfOmsetning !== 0 ? ((brfOmsetning - brfKost) / brfOmsetning) * 100 : 0;
  return { count, totalAmount, brfProsent, brfOmsetning, brfKost, antBestilt, antLevert };
}

const formatAmount = (n: number) =>
  new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

const formatPct = (n: number) =>
  new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const formatQty = (n: number) =>
  new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

// Same blue as the 'Alle ordre' card.
const DATE_COLOR = '#1976d2';
const DATE_BG = '#e3f2fd';

interface Props {
  stats: NO05StatusStat[];
  loading: boolean;
  selectedStatus: string;
  onSelect: (status: string) => void;
  showCrossFilters: boolean;
  onToggleCrossFilters: () => void;
  dateBuckets: DateBucket[];
  selectedDate: string;
  onSelectDate: (dateKey: string) => void;
  showDateFilters: boolean;
  onToggleDateFilters: () => void;
}

export default function NO05StatusCards({ stats, loading, selectedStatus, onSelect, showCrossFilters, onToggleCrossFilters, dateBuckets, selectedDate, onSelectDate, showDateFilters, onToggleDateFilters }: Props) {
  const groupedStats = useMemo(() => {
    const returStat      = stats.find(s => s.status === 'Kredit');
    const kontantStat    = stats.find(s => s.status === 'Kontant');
    const direkteStat    = stats.find(s => s.status === 'Direkte ordre');
    const plukkStat      = stats.find(s => s.status === 'Plukkordre');
      const bkCodeStat = stats.find(s => s.status === 'Ordre med BK kode');
      const returnToSupplierStat = stats.find(s => s.status === 'Retur til leverandør');
      const excluded = ['Kredit', 'Alle ordre', 'Kontant', 'Direkte ordre', 'Plukkordre', 'Ordre med BK kode', 'Retur til leverandør'];
    const nonReturnStats = stats.filter(s => !excluded.includes(s.status));

    return STATUS_GROUPS.map(group => {
      let agg;
      if (group.isReturn) {
        agg = returStat ? aggregateStats([returStat]) : aggregateStats([]);
      } else if (group.isKontant) {
        agg = kontantStat ? aggregateStats([kontantStat]) : aggregateStats([]);
      } else if (group.isDirekte) {
        agg = direkteStat ? aggregateStats([direkteStat]) : aggregateStats([]);
      } else if (group.isPlukk) {
        agg = plukkStat ? aggregateStats([plukkStat]) : aggregateStats([]);
      } else if (group.isBkCode) {
        agg = bkCodeStat ? aggregateStats([bkCodeStat]) : aggregateStats([]);
      } else if (group.isReturnToSupplier) {
        agg = returnToSupplierStat ? aggregateStats([returnToSupplierStat]) : aggregateStats([]);
      } else if (group.statuses === null) {
        // Alle ordre = all non-return stats
        agg = aggregateStats(nonReturnStats);
      } else {
        const matching = nonReturnStats.filter(s => statusInGroup(group, s.status));
        agg = aggregateStats(matching);
      }
      return { ...group, ...agg };
    });
  }, [stats]);

  // Shrunk from minmax(170,210) so 5 (or more, on a typical screen) fit on one row instead of 4.
  const gridColumns = 'repeat(auto-fill, minmax(148px, 180px))';

  if (loading) {
    return (
      <Box sx={{ p: 1, pb: 0.75, display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 72 }}>
        <CircularProgress size={22} thickness={5} sx={{ color: '#d30535' }} />
        <Typography variant="body2" color="text.secondary">Henter statuskort …</Typography>
      </Box>
    );
  }

  const selectedBucket = dateBuckets.find(b => b.key === selectedDate);

  const statusCards = groupedStats.filter(g => !g.isReturn && !g.isKontant && !g.isDirekte && !g.isPlukk && !g.isBkCode && !g.isReturnToSupplier && (g.name === 'Alle ordre' || g.count > 0));
  const crossCards  = groupedStats.filter(g => (g.isReturn || g.isKontant || g.isDirekte || g.isPlukk || g.isBkCode || g.isReturnToSupplier) && g.count > 0);

  return (
    <Box sx={{ p: 1, pb: 0.75 }}>
      <Box sx={{ mb: 0.5 }}>
        <Typography component="span" variant="subtitle2" fontWeight={700}>Status</Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          Ordre fordelt på status – hver ordre telles kun én gang
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 1 }}>
        {statusCards.map((group) => {
          const isSelected = selectedStatus === group.name ||
            (group.name === 'Alle ordre' && selectedStatus === '');
          return (
            <Card
              key={group.name}
              variant="outlined"
              sx={{
                borderColor: isSelected ? group.color : '#e0e0e0',
                borderWidth: isSelected ? 2 : 1,
                backgroundColor: isSelected ? group.bg : '#fff',
                boxShadow: isSelected ? `0 0 0 1px ${group.color}` : 'none',
                borderRadius: 1.5,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardActionArea onClick={() => onSelect(group.name)} sx={{ p: 0.65, height: '100%' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.25}>
                  <Chip
                    label={group.name}
                    size="small"
                    sx={{
                      fontSize: '0.62rem',
                      height: 15,
                      fontWeight: 600,
                      color: group.color,
                      backgroundColor: 'transparent',
                      border: `1px solid ${group.color}`,
                      '& .MuiChip-label': { px: 0.6 },
                    }}
                  />
                  <Typography fontWeight={700} lineHeight={1} sx={{ fontSize: '1.05rem' }}>
                    {group.count}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.66rem', lineHeight: 1.2 }}>
                  {formatAmount(group.totalAmount)} kr | Brf {formatPct(group.brfProsent)} %
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  {formatQty(group.antBestilt)} bestilt / {formatQty(group.antLevert)} levert
                </Typography>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {crossCards.length > 0 && (
        <>
          <Box
            onClick={onToggleCrossFilters}
            sx={{ mt: 1.5, mb: showCrossFilters ? 0.5 : 0, display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', userSelect: 'none', width: 'fit-content' }}
          >
            <Typography component="span" variant="subtitle2" fontWeight={700}>På tvers av status</Typography>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {showCrossFilters
                ? 'Utvalg som går på tvers – samme ordre kan inngå i flere bokser'
                : `${crossCards.length} filter${crossCards.length === 1 ? '' : 'e'} tilgjengelig`}
            </Typography>
            {showCrossFilters ? <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
          </Box>
          {showCrossFilters && (
          <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 1 }}>
            {crossCards.map((group) => {
              const isSelected = selectedStatus === group.name;
              return (
                <Card
                  key={group.name}
                  variant="outlined"
                  sx={{
                    borderColor: isSelected ? group.color : '#e0e0e0',
                    borderWidth: isSelected ? 2 : 1,
                    backgroundColor: isSelected ? group.bg : '#fff',
                    boxShadow: isSelected ? `0 0 0 1px ${group.color}` : 'none',
                    borderRadius: 1.5,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardActionArea onClick={() => onSelect(group.name)} sx={{ p: 0.65, height: '100%' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.25}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: isSelected ? group.color : 'text.primary' }}>
                        {group.name}
                      </Typography>
                      <Typography fontWeight={700} lineHeight={1} sx={{ fontSize: '1.05rem', color: isSelected ? group.color : 'text.primary' }}>
                        {group.count}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ color: isSelected ? group.color : 'text.secondary', fontSize: '0.66rem', lineHeight: 1.2 }}
                    >
                      {formatAmount(group.totalAmount)} kr | Brf {formatPct(group.brfProsent)} % | {formatQty(group.antBestilt)} bestilt / {formatQty(group.antLevert)} levert
                    </Typography>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
          )}
        </>
      )}

      {dateBuckets.length > 0 && (
        <>
          <Box
            onClick={onToggleDateFilters}
            sx={{ mt: 1.5, mb: showDateFilters ? 0.5 : 0, display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', userSelect: 'none', width: 'fit-content' }}
          >
            <Typography component="span" variant="subtitle2" fontWeight={700}>Ordredato</Typography>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {/* Collapsed with a day still selected: say so, since the status cards above are filtered by it. */}
              {showDateFilters || !selectedBucket
                ? 'Ordre fordelt på ordredato – siste 7 dager og eldre'
                : `Valgt: ${selectedBucket.label}`}
            </Typography>
            {showDateFilters ? <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />}
          </Box>
          {showDateFilters && (
          <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 1 }}>
            {dateBuckets.map((bucket) => {
              const isSelected = selectedDate === bucket.key;
              const isEmpty = bucket.count === 0;
              return (
                <Card
                  key={bucket.key}
                  variant="outlined"
                  sx={{
                    borderColor: isSelected ? DATE_COLOR : '#e0e0e0',
                    borderWidth: isSelected ? 2 : 1,
                    backgroundColor: isSelected ? DATE_BG : '#fff',
                    boxShadow: isSelected ? `0 0 0 1px ${DATE_COLOR}` : 'none',
                    borderRadius: 1.5,
                    opacity: isEmpty ? 0.6 : 1,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': isEmpty ? {} : {
                      transform: 'scale(1.02)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardActionArea disabled={isEmpty} onClick={() => onSelectDate(bucket.key)} sx={{ p: 0.65, height: '100%' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.25} gap={0.5}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: isSelected ? DATE_COLOR : 'text.primary' }}>
                        {bucket.label}
                      </Typography>
                      <Typography fontWeight={700} lineHeight={1} sx={{ fontSize: '1.05rem', color: isSelected ? DATE_COLOR : 'text.primary' }}>
                        {bucket.count}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: isSelected ? DATE_COLOR : 'text.secondary', fontSize: '0.66rem', lineHeight: 1.2 }}>
                      {formatAmount(bucket.totalAmount)} kr
                    </Typography>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
          )}
        </>
      )}
    </Box>
  );
}
